import type { ApiKeyCreatedResponse, ApiKeyResponse } from "@ccflare/api";
import { formatCost } from "@ccflare/core";
import { formatTokens } from "@ccflare/ui";
import { AlertCircle, AlertTriangle, Copy, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
	useApiKeys,
	useCreateApiKey,
	useDeleteApiKey,
	useGroups,
	useSetApiKeyActive,
	useSetApiKeyGroup,
} from "../hooks/queries";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";

function formatDate(iso: string | null): string {
	if (!iso) return "—";
	return new Date(iso).toLocaleString();
}

// ---- Create Dialog --------------------------------------------------------

interface CreateDialogProps {
	onClose: () => void;
	onCreated: (result: ApiKeyCreatedResponse) => void;
}

function CreateDialog({ onClose, onCreated }: CreateDialogProps) {
	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const create = useCreateApiKey();
	const { data: groups } = useGroups();
	const defaultGroup = groups?.find((g) => g.isDefault);
	const [groupId, setGroupId] = useState<string>("");

	// Once the default group is known, pre-select it
	const effectiveGroupId = groupId || defaultGroup?.id || "";

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		try {
			const result = await create.mutateAsync({
				name: name.trim(),
				groupId: effectiveGroupId || undefined,
			});
			onCreated(result);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to create key");
		}
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create API Key</DialogTitle>
					<DialogDescription>
						Give this key a descriptive name so you can identify it later.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="key-name">Key name</Label>
						<Input
							id="key-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. CI pipeline"
							required
							autoFocus
						/>
					</div>
					{groups && groups.length > 0 && (
						<div className="space-y-2">
							<Label htmlFor="key-group">Group</Label>
							<Select value={effectiveGroupId} onValueChange={setGroupId}>
								<SelectTrigger id="key-group">
									<SelectValue placeholder="Select group" />
								</SelectTrigger>
								<SelectContent>
									{groups.map((g) => (
										<SelectItem key={g.id} value={g.id}>
											{g.name}
											{g.isDefault ? " (default)" : ""}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}
					{error && (
						<p className="text-sm text-destructive flex items-center gap-1">
							<AlertCircle className="h-4 w-4" />
							{error}
						</p>
					)}
					<DialogFooter>
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" disabled={create.isPending || !name.trim()}>
							{create.isPending ? "Creating..." : "Create"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ---- Show-Once Dialog -----------------------------------------------------

interface ShowOnceDialogProps {
	result: ApiKeyCreatedResponse;
	onClose: () => void;
}

function ShowOnceDialog({ result, onClose }: ShowOnceDialogProps) {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		void navigator.clipboard.writeText(result.key).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>API Key Created</DialogTitle>
					<DialogDescription>
						Copy this key now. It will not be shown again.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="rounded-md border bg-muted/50 p-3">
						<p className="font-mono text-sm break-all select-all">
							{result.key}
						</p>
					</div>
					<div className="flex items-start gap-2 rounded-md border border-warning bg-warning/10 p-3">
						<AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
						<p className="text-sm text-warning-foreground">
							This key will not be shown again. Store it securely before closing
							this dialog.
						</p>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={handleCopy}>
						<Copy className="h-4 w-4 mr-2" />
						{copied ? "Copied!" : "Copy key"}
					</Button>
					<Button onClick={onClose}>Done</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---- Delete Confirm Dialog -------------------------------------------------

interface DeleteDialogProps {
	keyName: string;
	onConfirm: () => void;
	onCancel: () => void;
	loading: boolean;
}

function DeleteDialog({
	keyName,
	onConfirm,
	onCancel,
	loading,
}: DeleteDialogProps) {
	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Revoke API Key</DialogTitle>
					<DialogDescription>
						Are you sure you want to permanently revoke{" "}
						<strong>{keyName}</strong>? Any clients using this key will
						immediately lose access.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="outline" onClick={onCancel} disabled={loading}>
						Cancel
					</Button>
					<Button variant="destructive" onClick={onConfirm} disabled={loading}>
						{loading ? "Revoking..." : "Revoke"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---- Main Tab -------------------------------------------------------------

export function ApiKeysTab() {
	const { data: keys, isLoading } = useApiKeys();
	const { data: groups } = useGroups();
	const setActive = useSetApiKeyActive();
	const setKeyGroup = useSetApiKeyGroup();
	const deleteKey = useDeleteApiKey();

	const [showCreate, setShowCreate] = useState(false);
	const [showOnce, setShowOnce] = useState<ApiKeyCreatedResponse | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<ApiKeyResponse | null>(null);

	const handleToggleActive = (key: ApiKeyResponse) => {
		void setActive.mutateAsync({ id: key.id, active: !key.active });
	};

	const handleGroupChange = (key: ApiKeyResponse, newGroupId: string) => {
		void setKeyGroup.mutateAsync({ id: key.id, groupId: newGroupId });
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		await deleteKey.mutateAsync({ id: deleteTarget.id });
		setDeleteTarget(null);
	};

	if (isLoading) {
		return (
			<Card>
				<CardContent className="pt-6">
					<p className="text-muted-foreground">Loading API keys...</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>API Keys</CardTitle>
							<CardDescription>
								Manage gateway API keys. Keys are shown only once at creation.
							</CardDescription>
						</div>
						<Button onClick={() => setShowCreate(true)} size="sm">
							<Plus className="mr-2 h-4 w-4" />
							Create Key
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{!keys?.length ? (
						<p className="text-sm text-muted-foreground py-4 text-center">
							No API keys yet. Create one to grant client access.
						</p>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b text-muted-foreground text-left">
										<th className="pb-2 pr-4 font-medium">Name</th>
										<th className="pb-2 pr-4 font-medium">Key</th>
										<th className="pb-2 pr-4 font-medium">Group</th>
										<th className="pb-2 pr-4 font-medium">Active</th>
										<th className="pb-2 pr-4 font-medium">Requests</th>
										<th className="pb-2 pr-4 font-medium">Tokens</th>
										<th
											className="pb-2 pr-4 font-medium"
											title="Money actually drawn (€0 while within plan limits)"
										>
											Cost
										</th>
										<th
											className="pb-2 pr-4 font-medium"
											title="What these tokens would cost via the Anthropic API"
										>
											Theoretical
										</th>
										<th className="pb-2 pr-4 font-medium">Created</th>
										<th className="pb-2 pr-4 font-medium">Last Used</th>
										<th className="pb-2 font-medium" />
									</tr>
								</thead>
								<tbody className="divide-y divide-border">
									{keys.map((key) => (
										<tr key={key.id} className="hover:bg-muted/30">
											<td className="py-3 pr-4 font-medium">{key.name}</td>
											<td className="py-3 pr-4">
												<span className="font-mono text-xs text-muted-foreground">
													{key.prefix}…{key.last4}
												</span>
											</td>
											<td className="py-3 pr-4">
												{groups && groups.length > 1 ? (
													<Select
														value={key.groupId}
														onValueChange={(newId) =>
															handleGroupChange(key, newId)
														}
														disabled={setKeyGroup.isPending}
													>
														<SelectTrigger className="h-7 w-32 text-xs">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															{groups.map((g) => (
																<SelectItem
																	key={g.id}
																	value={g.id}
																	className="text-xs"
																>
																	{g.name}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												) : (
													<span className="text-muted-foreground">
														{key.groupName ?? "—"}
													</span>
												)}
											</td>
											<td className="py-3 pr-4">
												<Switch
													checked={key.active}
													onCheckedChange={() => handleToggleActive(key)}
													disabled={setActive.isPending}
												/>
											</td>
											<td className="py-3 pr-4">
												{key.totalRequests.toLocaleString()}
											</td>
											<td className="py-3 pr-4">
												{formatTokens(key.totalTokens)}
											</td>
											<td className="py-3 pr-4">
												{formatCost(key.totalCostUsd)}
											</td>
											<td className="py-3 pr-4 text-muted-foreground">
												{formatCost(key.totalTheoreticalCostUsd)}
											</td>
											<td className="py-3 pr-4 text-muted-foreground text-xs">
												{formatDate(key.createdAt)}
											</td>
											<td className="py-3 pr-4 text-muted-foreground text-xs">
												{formatDate(key.lastUsed)}
											</td>
											<td className="py-3">
												{key.active ? (
													<Badge variant="success" className="text-xs">
														Active
													</Badge>
												) : (
													<Badge variant="secondary" className="text-xs">
														Disabled
													</Badge>
												)}
												<Button
													variant="ghost"
													size="sm"
													className="ml-2 text-destructive hover:text-destructive"
													onClick={() => setDeleteTarget(key)}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			{showCreate && (
				<CreateDialog
					onClose={() => setShowCreate(false)}
					onCreated={(result) => {
						setShowCreate(false);
						setShowOnce(result);
					}}
				/>
			)}

			{showOnce && (
				<ShowOnceDialog result={showOnce} onClose={() => setShowOnce(null)} />
			)}

			{deleteTarget && (
				<DeleteDialog
					keyName={deleteTarget.name}
					onConfirm={() => void handleDelete()}
					onCancel={() => setDeleteTarget(null)}
					loading={deleteKey.isPending}
				/>
			)}
		</div>
	);
}
